<template>
  <div class="auth-code-container">
    <div v-for="input in props.codeLength" :key="input - 1" class="auth-code-input-box">
      <input
        type="text"
        :class="['auth-code-input', { error: props.isError }]"
        @keydown="(event: Event) => onKeyDown(event, input - 1)"
        @paste.prevent="(event: Event) => onPaste(event, input - 1)"
        @beforeinput.prevent="(event: Event) => onBeforeInput(event, input - 1)"
        ref="authInputs"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeMount, onMounted, ref, watch } from 'vue';

// TODO: Add explanation for certain decisions
// - Prevent modifier on paste and beforeinput event
// - Why three different events
// - Why the watch
// - Why paste not handled in beforeinput

// Todo: Last thing to tackle are internal value updates of this component. At the moment, everything seems to work,
// but i think that the value gets updated, then the emit happens, then the watch gets triggered for the model value,
// which then updates the inputs again. This is not a problem, but it is not very clean. I think it is possible to
// prevent this from happening by using a ref for the modelValue and only updating the inputs when the ref changes.
// This way, the watch will not trigger when the inputs are updated, because the modelValue will not change.
// This is not a priority, but it would be nice to fix this.

const authInputs = ref<HTMLInputElement[]>([]);

const emit = defineEmits<{
  'update:modelValue': [value: string[]]
  submit: [];
}>();

const props = defineProps<{
  codeLength: number;
  modelValue: string[];
  isError?: boolean;
}>();

// Check if modelValue is valid when changed from outside. This works in sort of a recursive way.
watch(() => props.modelValue, value => {
  // Provided modelValue is valid (input array matches code length and each array item is a single digit)
  if (value.length === props.codeLength && value.every(char => char.match(/^\d?$/))) {
    authInputs.value.forEach((input, index) => input.value = value[index]);
    return;
  }

  // Provided modelValue is valid but too short
  if (value.length < props.codeLength && value.every(char => char.match(/^\d?$/))) {
    emit('update:modelValue', Array.from({ length: props.codeLength }, (item, index) => value[index] ?? ''));
    return;
  }

  // Other cases are considered invalid
  emit('update:modelValue', authInputs.value.map(input => input.value));
});

// Order of events: keydown => paste => beforeinput => input

// User uses Enter or Backspace
function onKeyDown(event: Event, index: number) {
  if (!(event instanceof KeyboardEvent && event.target instanceof HTMLInputElement)) return;

  if (event.key === 'Enter') {
    if (props.modelValue.every(value => value)) emit('submit');
    event.preventDefault();
  } else if (event.key === 'Backspace') {
    event.target.value = '';
    emit('update:modelValue', authInputs.value.map(input => input.value));
    authInputs.value[Math.max(index - 1, 0)].select();
    event.preventDefault();
  }
}

// User pastes in a string of digits
function onPaste(event: Event, index: number) {
  if (!(event instanceof ClipboardEvent && event.target instanceof HTMLInputElement)) return;

  const value = event.clipboardData ? event.clipboardData.getData('text').trim() : '';
  if (value.match(/^\d+$/)) handlePaste(value, index);
}

// User types in single digits
function onBeforeInput(event: Event, index: number) {
  if (!(event instanceof InputEvent && event.target instanceof HTMLInputElement)) return;

  if (event.data && event.data.match(/^\d$/)) {
    event.target.value = event.data;
    emit('update:modelValue', authInputs.value.map(input => input.value));
    authInputs.value[Math.min(index + 1, props.codeLength - 1)].select();
  }

  // Some autocomplete services add the code through beforeinput instead of paste
  else if (event.data && event.data.match(/^\d+$/)) {
    handlePaste(event.data, index);
  }
}

function handlePaste(value: string, index: number) {
  if (value.length === props.codeLength) {
    authInputs.value.forEach((input, index) => input.value = value[index]);
    emit('update:modelValue', authInputs.value.map(input => input.value));
    authInputs.value[props.codeLength - 1].select();
  } else if (index + value.length <= props.codeLength) {
    value.split('').forEach((char, i) => authInputs.value[index + i].value = char);
    emit('update:modelValue', authInputs.value.map(input => input.value));
    authInputs.value[Math.min(index + value.length, props.codeLength - 1)].select();
  }
}

onBeforeMount(() => emit('update:modelValue', Array.from({ length: props.codeLength }, () => '')));
onMounted(() => authInputs.value[0].focus());
</script>

<style scoped lang="scss">
.auth-code-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
}

.auth-code-input-box {
  &:last-child {
    margin-right: 0;
  }
}

.auth-code-input {
  width: 45px;
  height: 45px;
  line-height: 45px;
  font-size: 24px;
  padding: 0px;
  text-align: center;
  color: #494949;
  font-weight: 400;
  border: 1px solid #d6d6d6;
  border-radius: 4px;
  background: #fff;
  box-sizing: border-box;
  -webkit-appearance: none;

  &:focus {
    outline: none;
    border: 1px solid #97cee5;
    box-shadow: 0 0 0 3px rgba(131, 192, 253, 0.5);
  }
}

.error {
  border: 1px solid red;
}
</style>
